/** Etykiety i warianty chipów — nazwy 1:1 z mockupami `design/screens/*.png`. */

import type { StatusVariant } from '@/design/components'
import { daysUntil, formatDate } from '@/lib/format'
import type { DocumentSource, DocumentStatus, RoutingStage } from './types'

/** Próg pewności modelu (backend: `CONFIDENCE_THRESHOLD=0.80`). */
export const CONFIDENCE_THRESHOLD = 0.8

export const STATUS_LABEL: Record<DocumentStatus, string> = {
  received: 'W kolejce AI',
  processing_ocr: 'OCR w toku',
  processing_llm: 'Analiza AI',
  pending_verification: 'Do weryfikacji',
  low_confidence: 'Niska pewność',
  verified: 'Zweryfikowany',
  routing: 'W dekretacji',
  routed: 'Zdekretowany',
  // `failed` to AWARIA pipeline'u (OCR/AI), `rejected` — decyzja człowieka.
  // Wcześniej oba miały etykietę „Odrzucony", więc błąd techniczny wyglądał
  // jak świadoma odmowa kancelarii i nikt go nie ponawiał.
  failed: 'Błąd przetwarzania',
  rejected: 'Odrzucony',
  duplicate: 'Duplikat',
}

export const STATUS_VARIANT: Record<DocumentStatus, StatusVariant> = {
  received: 'info',
  processing_ocr: 'info',
  processing_llm: 'info',
  pending_verification: 'warning',
  low_confidence: 'danger',
  verified: 'success',
  routing: 'info',
  routed: 'neutral',
  failed: 'danger',
  rejected: 'neutral',
  duplicate: 'neutral',
}

export const SOURCE_LABEL: Record<DocumentSource, string> = {
  edoreczenia: 'e-Doręczenia',
  epuap: 'ePUAP',
  scan: 'Skan',
  email: 'E-mail',
  upload: 'Przesłany',
}

/** Kolejność pigułek filtra źródeł (jak na ekranie skrzynki). */
export const SOURCE_ORDER: DocumentSource[] = ['edoreczenia', 'epuap', 'scan', 'email', 'upload']

export const STAGE_LABEL: Record<RoutingStage, string> = {
  inbox: 'Wpływ — do dekretacji',
  partner_review: 'U partnera — akceptacja',
  assigned: 'Przekazane prawnikowi',
}

export const STAGE_ORDER: RoutingStage[] = ['inbox', 'partner_review', 'assigned']

/** Następny etap obiegu (`null` = koniec ścieżki). */
export function nextStage(stage: RoutingStage | null): RoutingStage | null {
  if (stage === null || stage === 'inbox') return 'partner_review'
  if (stage === 'partner_review') return 'assigned'
  return null
}

/** Statusy końcowe, z których dokument da się zawrócić do kolejki (`POST /retry`). */
export const RETRYABLE_STATUSES: DocumentStatus[] = ['failed', 'rejected']

export function canRetry(status: DocumentStatus): boolean {
  return RETRYABLE_STATUSES.includes(status)
}

/** Pola pokazywane monospace'em (sygnatury, kwoty, znaki spraw). */
export const MONO_FIELD_KEYS = new Set(['signature', 'amount', 'reference'])

/** „dziś 09:14" / „wczoraj 16:30" / „05.08.2026" — druga linia kolumny DOKUMENT. */
export function receivedShort(value: string | null | undefined): string {
  if (!value) return '—'
  const days = daysUntil(value)
  const time = new Date(value).toLocaleTimeString('pl-PL', {
    hour: '2-digit',
    minute: '2-digit',
  })
  if (days === 0) return `dziś ${time}`
  if (days === -1) return `wczoraj ${time}`
  return formatDate(value)
}
